import { ArrowLeftIcon, Button, LockIcon, TabBar, TabBarItem } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useMatches, useNavigate } from 'react-router-dom';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { BlockPhase } from '../../../pages/Timetable/Plan/TimetableVersion/TimetableVersionLayout';
import { addLeadingZero } from '../../../utils/helper';
import { useTeachingBlockStore } from '../../TeachingBlockGrid/Contexts/TeachingBlockContext';
import { useTeachingBlockForVersion } from '../../Teachingblock/hooks/useTeachingBlocks';
import { useTeachingBlockVersionData } from '../hooks/useTeachingBlockVersionData';
import styles from './TeachingBlockVersionTopBar.module.scss';

interface RouteData {
  activeTimetableVersionTab?: string;
}

export const TeachingBlockVersionTopBar = observer(
  ({
    versionUuid,
    baseSlug,
    blockPhase = 'running',
    onStartFix,
    onCompleteFix,
  }: {
    versionUuid: string;
    baseSlug: string;
    blockPhase?: BlockPhase;
    onStartFix?: () => Promise<void>;
    onCompleteFix?: () => Promise<boolean>;
  }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const store = useTeachingBlockStore();

    const matches = useMatches();
    const active = matches.find((match) => {
      const data = match.data as RouteData | undefined;
      return Boolean(data?.activeTimetableVersionTab);
    })?.data as RouteData | undefined;

    const activeTab = active?.activeTimetableVersionTab;

    const tabs: TabBarItem[] = [
      {
        trigger: t('teachingBlockVersion.teachingBlockData'),
        id: 'data',
        content: '',
      },
      {
        trigger: t('pinboard.title', { count: 1 }),
        id: 'board',
        content: '',
      },
    ];

    const { versionData } = useTeachingBlockVersionData(versionUuid);

    const { teachingBlock: currentTeachingBlock } = useTeachingBlockForVersion(versionUuid);

    const { subjectContainerData } = useLoadBasicData({ pause: !currentTeachingBlock });

    const currentSubjectContainer = subjectContainerData?.subjectContainers.find((sc) => {
      return currentTeachingBlock?.subjectContainerUuid === sc.uuid;
    });

    return (
      <div className={styles['teaching-block-version-top-bar']}>
        <div className={styles.header}>
          <Button
            className={styles.back}
            icon={<ArrowLeftIcon />}
            hierarchy='tertiary'
            onClick={() => navigate('/' + t('routes.timetable.slug') + '/' + t('routes.plan.slug'), { replace: true })}
          />
          <div className={styles.headline}>
            {currentSubjectContainer?.name} - v{addLeadingZero(versionData?.meta.version ?? '')}
          </div>
          <div className={styles.description}>
            {currentSubjectContainer?.grades && currentSubjectContainer?.grades.length > 0
              ? t('grades.title', { count: 2 }) + ': ' + currentSubjectContainer?.grades.join(', ')
              : t('common.all') + ' ' + t('grades.title', { count: 2 })}
          </div>
          {(onStartFix || onCompleteFix) &&
            store.currentVersion?.active &&
            blockPhase !== null &&
            (store.temporarilyEditable ? (
              <Button
                icon={<LockIcon className='small' />}
                hierarchy='primary'
                onClick={async () => {
                  // Edit-Status nur zurücksetzen, wenn die Aktion nicht (z.B. wegen
                  // eines laufenden EventJobs) abgebrochen wurde.
                  if (await onCompleteFix?.()) {
                    store.setTemporarilyEditable(false);
                  }
                }}
              >
                {t('timetableVersion.completeFix')}
              </Button>
            ) : (
              <Button
                hierarchy='secondary'
                onClick={async () => {
                  store.setTemporarilyEditable(true);
                  try {
                    await onStartFix?.();
                  } catch {
                    // Fix fehlgeschlagen -> temporären Edit-Status zurücksetzen.
                    store.setTemporarilyEditable(false);
                  }
                }}
              >
                {t('timetableVersion.fixVersion')}
              </Button>
            ))}
        </div>
        <TabBar
          activeTabId={activeTab}
          className={styles['tab-bar']}
          tabs={tabs}
          onValueChange={(type) => {
            if (type === 'data') {
              navigate(
                `/${baseSlug}/${t('routes.teachingBlockVersions.slug')}/${versionUuid}/${t('routes.versionPages.data.slug')}/${t(
                  'routes.versionsFilter.class.slug',
                )}`,
                { replace: true },
              );
            } else {
              navigate(
                `/${baseSlug}/${t('routes.teachingBlockVersions.slug')}/${versionUuid}/${t('routes.versionPages.board.slug')}/`,
                { replace: true },
              );
            }
          }}
        />
      </div>
    );
  },
);
